import { useState, useEffect } from "react";
import { api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Clock, MapPin, IndianRupee, RefreshCw, Building2, Bus, Car, House, UmbrellaIcon, Briefcase } from "lucide-react";

const TYPE_ICON = {
  hotel: Building2, homestay: House, villa: UmbrellaIcon,
  cab: Car, bus: Bus, package: Briefcase,
};

export default function PendingListingsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/listings/pending");
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load pending listings");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const approve = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/admin/listings/${id}/approve`);
      toast.success("Listing approved — partner is now live");
      setItems(prev => prev.filter(x => x.id !== id));
    } catch (e) { toast.error(e.response?.data?.detail || "Approve failed"); }
    finally { setBusyId(null); }
  };

  const reject = async (id) => {
    const reason = window.prompt("Reason for rejection (sent to partner)?", "Photos are missing");
    if (reason === null) return;
    setBusyId(id);
    try {
      await api.post(`/admin/listings/${id}/reject`, { reason: reason.trim() });
      toast.success("Listing rejected");
      setItems(prev => prev.filter(x => x.id !== id));
    } catch (e) { toast.error(e.response?.data?.detail || "Reject failed"); }
    finally { setBusyId(null); }
  };

  return (
    <div className="space-y-3" data-testid="pending-listings-tab">
      <div className="bg-[#FEF3C7] border border-[#FDE68A] rounded-xl p-4 flex items-start gap-3">
        <Clock className="w-5 h-5 text-[#B45309] mt-0.5 flex-shrink-0" />
        <div className="text-sm text-[#78350F] flex-1">
          <p className="font-semibold">Partner-submitted listings awaiting approval</p>
          <p className="text-xs mt-1 text-[#92400E]">When you approve, the listing goes live instantly on safarsetu.com. Rejected partners can edit &amp; re-submit; the reason you enter is emailed to them.</p>
        </div>
        <Button size="sm" variant="ghost" className="rounded-full text-xs" onClick={load} data-testid="refresh-pending">
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {loading && <p className="text-sm text-[#8A998E] text-center py-8">Loading…</p>}

      {!loading && items.length === 0 && (
        <div className="text-center py-12 text-[#8A998E] bg-white rounded-xl border border-[#E5E3DB]">
          <CheckCircle2 className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="font-medium">No listings awaiting approval</p>
          <p className="text-xs mt-1">All caught up! New partner submissions appear here.</p>
        </div>
      )}

      {!loading && items.map(l => {
        const Icon = TYPE_ICON[l.type] || Building2;
        return (
          <div key={l.id} className="bg-white rounded-xl border border-[#E5E3DB] p-4" data-testid={`pending-listing-${l.id}`}>
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-lg overflow-hidden bg-[#F3F2EE] flex items-center justify-center flex-shrink-0">
                {l.images && l.images[0]
                  ? <img src={l.images[0]} alt={l.title} className="w-full h-full object-cover" />
                  : <Icon className="w-7 h-7 text-[#5C6B60]" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div>
                    <h4 className="font-['Outfit'] font-semibold text-[#1A231C] text-sm" data-testid={`listing-title-${l.id}`}>{l.title}</h4>
                    <p className="text-xs text-[#5C6B60] mt-0.5">{l.host_name || "Unknown host"} · {l.host_id?.slice(0, 8)}</p>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <Button size="sm" className="rounded-full bg-green-600 hover:bg-green-700 text-white text-xs disabled:opacity-50" disabled={busyId === l.id} onClick={() => approve(l.id)} data-testid={`approve-listing-${l.id}`}>
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-full text-red-600 border-red-200 hover:bg-red-50 text-xs disabled:opacity-50" disabled={busyId === l.id} onClick={() => reject(l.id)} data-testid={`reject-listing-${l.id}`}>
                      <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                    </Button>
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-2 flex-wrap text-xs text-[#5C6B60]">
                  <Badge className="bg-amber-100 text-amber-700 border-0 text-xs capitalize">Pending</Badge>
                  <Badge className="bg-[#2C5F2D]/10 text-[#2C5F2D] border-0 text-xs capitalize">{l.type}</Badge>
                  {l.star_rating && <Badge className="bg-yellow-100 text-yellow-800 border-0 text-xs">{"★".repeat(l.star_rating)}</Badge>}
                  <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{l.location || `${l.from_city} → ${l.to_city}`}</span>
                  <span className="flex items-center gap-1"><IndianRupee className="w-3 h-3" />{l.price?.toLocaleString("en-IN")}</span>
                  <span className="ml-auto">Submitted {(l.submitted_at || l.created_at || "").slice(0, 10)}</span>
                </div>
                {l.description && (
                  <p className="mt-2 text-xs text-[#5C6B60] line-clamp-3 bg-[#F8F7F2] rounded-lg p-3" data-testid={`listing-desc-${l.id}`}>{l.description}</p>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
